import { Formik } from 'formik';
import { Alert, Button, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { unitCategory } from 'goodmaps-utils';
import useRoomQueryHook from './useRoomQueryHook';
import useRoomEditMutation from './useRoomEditMutation';
import * as Yup from 'yup';
import './RoomEditModal.scss';

type Props = {
  roomId: string;
  onClose: () => void;
};

export const editRoomFormSchema = Yup.object({
  name: Yup.string().notRequired(),
  spanishName: Yup.string().notRequired(),
  category: Yup.string().required('Please provide a valid category.'),
});

export type editRoomFormProperties = {
  name: string;
  spanishName: string;
  category: string;
};

const RoomEditModal = ({ roomId, onClose }: Props) => {
  const { getRoomsQuery, isQueryLoading, roomName, populatingFormError, categories, initialValues } = useRoomQueryHook({ roomId });

  const { onConfirmRoomEdit, isRoomMutationLoading, editRoomData, editRoomError } = useRoomEditMutation({ roomId, getRoomsQuery });

  const editSuccessMessage = () => {
    return editRoomData ? <Alert variant="success">{`Successfully Edited Room: ${editRoomData.id}`}</Alert> : null;
  };

  const editErrors = () => {
    return editRoomError ? <Alert variant="danger">{`Unsuccessful Edit: ${editRoomError}`}</Alert> : null;
  };

  return (
    <div className="edit-room-modal-body">
      <Modal.Header closeButton>
        <Modal.Title>{isQueryLoading || populatingFormError ? null : `Edit Room: ${roomName}`}</Modal.Title>
      </Modal.Header>
      {isQueryLoading ? (
        <div className="loader-container">
          <Spinner animation="border" />
        </div>
      ) : (
        <Modal.Body>
          {populatingFormError ? (
            <Alert variant="danger">{`Error: ${populatingFormError}`}</Alert>
          ) : (
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={editRoomFormSchema}
              onSubmit={values => {
                onConfirmRoomEdit(values);
              }}
            >
              {formik => (
                <Form id="editRoomForm" onSubmit={formik.handleSubmit}>
                  <Row>
                    <Form.Group>
                      <Form.Label>Name*</Form.Label>
                      <Form.Control as="input" id="name" {...formik.getFieldProps('name')} />
                      {formik.touched.name && formik.errors.name ? <div className="form-feedback-error">{formik.errors.name}</div> : null}
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group>
                      <Form.Label>Spanish Name</Form.Label>
                      <Form.Control as="input" id="spanishName" {...formik.getFieldProps('spanishName')} />
                      {formik.touched.spanishName && formik.errors.spanishName ? (
                        <div className="form-feedback-error">{formik.errors.spanishName}</div>
                      ) : null}
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group>
                      <Form.Label>Category*</Form.Label>
                      <Form.Select id="category" {...formik.getFieldProps('category')}>
                        <option value="">Select A Category</option>
                        {categories.map((category: unitCategory) => {
                          return (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          );
                        })}
                      </Form.Select>
                      {formik.touched.category && formik.errors.category ? <div className="form-feedback-error">{formik.errors.category}</div> : null}
                    </Form.Group>
                  </Row>
                  {editSuccessMessage()}
                  {editErrors()}
                  <hr></hr>
                  <div className="footer">
                    <Button style={{ marginRight: '0.25rem' }} onClick={() => onClose()} variant="secondary">
                      Close
                    </Button>
                    <Button
                      form="editRoomForm"
                      type="submit"
                      variant="primary"
                      disabled={isRoomMutationLoading || !(!isQueryLoading && !populatingFormError) || !formik.dirty}
                    >
                      {isRoomMutationLoading ? <Spinner animation="border" /> : 'Confirm'}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </Modal.Body>
      )}
    </div>
  );
};

export default RoomEditModal;
